/**
 * Pure-logic layer for the canonical widget-fabric taxonomy.
 *
 * Kept React-free (like `widget-descriptor.ts`) so the coverage assertion can
 * be unit-tested under `node --test` without dragging React into the runtime
 * graph. The React rendering layer lives in `./canonical/*`.
 *
 * P4-32 / P7-2: every module landing page must expose at least one widget of
 * each canonical kind — `status`, `work-queue`, and `exception`. This file
 * exposes the taxonomy plus the assertion a module (or its test) calls over its
 * own descriptor array to prove that guarantee holds.
 */

// Type-only import so this pure module stays runtime-independent of the
// `@tyndo/types` barrel (whose extensionless re-exports don't resolve under the
// `node --test` strip-types loader). The canonical tuple is owned here, at the
// widget-fabric layer.
import type { WidgetDescriptor, WidgetKind } from '@tyndo/types';

export type { WidgetKind };

/**
 * The three canonical kinds every module landing page must expose. Kept as a
 * runtime tuple so the coverage assertion can iterate it.
 */
export const CANONICAL_WIDGET_KINDS = ['status', 'work-queue', 'exception'] as const;

/** The canonical kinds as a plain readonly array for iteration in consumers. */
export const CANONICAL_KIND_LIST: readonly WidgetKind[] = CANONICAL_WIDGET_KINDS;

export interface CanonicalCoverageResult {
  /** True when every canonical kind is present at least once. */
  ok: boolean;
  /** Canonical kinds with no matching descriptor. */
  missing: WidgetKind[];
  /** Map of canonical kind → the widgetIds that satisfy it. */
  byKind: Record<string, string[]>;
}

/**
 * Assert that a module's widget descriptors cover all three canonical kinds.
 *
 * A descriptor contributes to a canonical kind when its `kind` field equals
 * that kind. Descriptors with `metric` / `custom` / no kind are ignored for
 * coverage (they are legitimate extras).
 *
 * Returns a structured result rather than throwing so callers can decide how
 * to surface a gap (a test asserts `.ok`; a dev-time console can list
 * `.missing`).
 */
export function assertCanonicalWidgetCoverage(
  descriptors: readonly Pick<WidgetDescriptor, 'widgetId' | 'kind'>[],
): CanonicalCoverageResult {
  const byKind: Record<string, string[]> = {};
  for (const kind of CANONICAL_WIDGET_KINDS) byKind[kind] = [];

  for (const d of descriptors) {
    if (!d.kind) continue;
    const bucket = byKind[d.kind];
    if (bucket) bucket.push(d.widgetId);
  }

  const missing = CANONICAL_WIDGET_KINDS.filter((k) => (byKind[k]?.length ?? 0) === 0);
  return { ok: missing.length === 0, missing: [...missing], byKind };
}
